import React from "react";
import { RiBookOpenLine } from "react-icons/ri";
import {
  LuBadgeInfo,
  LuBookText,
  LuClipboardCheck,
  LuFileText,
  LuRefreshCw,
  LuShieldCheck,
  LuTriangleAlert,
  LuUserRoundCheck,
} from "react-icons/lu";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useLanguage } from "../context/LanguageContext";
import { POLICIES_PAGE_CONTENT } from "../content/policiesContent";
import "../styles/Policies.scss";

const ICONS_BY_KEY = {
  book: RiBookOpenLine,
  info: LuBadgeInfo,
  terms: LuBookText,
  shield: LuShieldCheck,
  privacy: LuShieldCheck,
  risk: LuTriangleAlert,
  minor: LuUserRoundCheck,
  handover: LuClipboardCheck,
  document: LuFileText,
  refresh: LuRefreshCw,
};

const renderBlock = (block, sectionId, index) => {
  const key = `${sectionId}-${block.type}-${index}`;

  if (block.type === "heading") {
    return (
      <h4 key={key} className="policies-card__subheading">
        {block.text}
      </h4>
    );
  }

  if (block.type === "paragraph") {
    return <p key={key}>{block.text}</p>;
  }

  if (block.type === "note") {
    return (
      <div key={key} className="policies-card__note">
        <p>{block.text}</p>
      </div>
    );
  }

  if (block.type === "meta") {
    return (
      <ul key={key} className="policies-card__meta">
        {block.items.map((item) => (
          <li key={`${key}-${item.label}`}>
            <strong>{item.label}:</strong> <span>{item.value}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (block.type === "list") {
    const ListTag = block.ordered ? "ol" : "ul";
    return (
      <ListTag key={key} className="policies-card__list">
        {block.items.map((item) => (
          <li key={`${key}-${item}`}>{item}</li>
        ))}
      </ListTag>
    );
  }

  return null;
};

const PoliciesPage = () => {
  const { language } = useLanguage();
  const locale = language === "de" ? "de" : "en";
  const pageContent = POLICIES_PAGE_CONTENT[locale];
  const legalSections = pageContent.sections;
  const heroHighlights = pageContent.highlights;
  const sectionRefs = React.useRef({});
  const [activeSection, setActiveSection] = React.useState(legalSections[0]?.id ?? "");
  const navRef = React.useRef(null);
  const layoutRef = React.useRef(null);
  const navUpdateRef = React.useRef(() => {});

  React.useEffect(() => {
    if (!legalSections.some((section) => section.id === activeSection)) {
      setActiveSection(legalSections[0]?.id ?? "");
    }
  }, [activeSection, legalSections]);

  React.useEffect(() => {
    const sectionNodes = legalSections
      .map((section) => sectionRefs.current[section.id])
      .filter((node) => node instanceof Element);

    if (sectionNodes.length === 0) {
      return undefined;
    }

    const updateActiveSection = () => {
      const activationOffset = Math.min(220, Math.max(120, window.innerHeight * 0.22));
      let currentSectionId = sectionNodes[0].id;

      sectionNodes.forEach((node) => {
        if (node.getBoundingClientRect().top <= activationOffset) {
          currentSectionId = node.id;
        }
      });

      setActiveSection((prev) => (prev === currentSectionId ? prev : currentSectionId));
    };

    updateActiveSection();
    window.addEventListener("scroll", updateActiveSection, { passive: true });
    window.addEventListener("resize", updateActiveSection);
    window.addEventListener("hashchange", updateActiveSection);

    return () => {
      window.removeEventListener("scroll", updateActiveSection);
      window.removeEventListener("resize", updateActiveSection);
      window.removeEventListener("hashchange", updateActiveSection);
    };
  }, [legalSections]);

  const handleNavClick = React.useCallback((event, sectionId) => {
    event.preventDefault();

    const node = sectionRefs.current[sectionId];
    if (!(node instanceof Element)) {
      return;
    }

    setActiveSection(sectionId);
    window.history.replaceState(null, "", `#${sectionId}`);
    node.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  React.useEffect(() => {
    const navEl = navRef.current;
    const layoutEl = layoutRef.current;
    if (!navEl || !layoutEl) {
      return undefined;
    }

    const state = { current: 0, target: 0 };
    navEl.style.setProperty("--nav-shift", "0px");

    let ticking = null;
    let animationFrame = null;

    const updateTarget = () => {
      if (!navRef.current || !layoutRef.current) {
        return;
      }

      const layoutRect = layoutRef.current.getBoundingClientRect();
      const navRect = navRef.current.getBoundingClientRect();
      const navHalf = navRect.height / 2;
      const viewportCenter = window.innerHeight / 2;
      const margin = 18;

      let clampTop = layoutRect.top + margin + navHalf;
      let clampBottom = layoutRect.bottom - margin - navHalf;

      if (clampBottom < clampTop) {
        const middle = (clampTop + clampBottom) / 2;
        clampTop = middle;
        clampBottom = middle;
      }

      const desiredCenter = Math.min(Math.max(viewportCenter, clampTop), clampBottom);
      const currentCenter = navRect.top + navHalf;
      const delta = desiredCenter - currentCenter;

      state.target = state.current + delta;
    };

    const smoothStep = () => {
      if (!navRef.current) {
        return;
      }
      state.current += (state.target - state.current) * 0.1;
      if (Math.abs(state.target - state.current) < 0.15) {
        state.current = state.target;
      }
      navRef.current.style.setProperty("--nav-shift", `${state.current}px`);
      animationFrame = window.requestAnimationFrame(smoothStep);
    };

    smoothStep();

    const scheduleUpdate = () => {
      if (ticking != null) {
        return;
      }
      ticking = window.requestAnimationFrame(() => {
        ticking = null;
        updateTarget();
      });
    };

    scheduleUpdate();

    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    navUpdateRef.current = scheduleUpdate;

    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
      if (ticking) {
        window.cancelAnimationFrame(ticking);
      }
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      navUpdateRef.current = () => {};
    };
  }, []);

  React.useEffect(() => {
    navUpdateRef.current?.();
  }, [activeSection]);

  return (
    <div className="policies-page">
      <Header />
      <main className="policies-content">
        <section className="policies-hero">
          <div className="policies-hero__inner">
          <div className="policies-hero__copy">
              <span className="policies-hero__eyebrow">{pageContent.hero.eyebrow}</span>
              <h1>{pageContent.hero.title}</h1>
              <p>{pageContent.hero.description}</p>
            </div>
            <ul className="policies-hero__highlights">
              {heroHighlights.map((point) => {
                const Icon = ICONS_BY_KEY[point.iconKey] ?? RiBookOpenLine;
                return (
                  <li key={point.title} className={`accent-${point.accent}`}>
                    <span className="policies-hero__icon">
                      <Icon aria-hidden="true" />
                    </span>
                    <div>
                      <strong>{point.title}</strong>
                      <p>{point.description}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="policies-hero__wave" aria-hidden="true" />
        </section>

        <div className="policies-layout" ref={layoutRef}>
          <nav className="policies-nav" aria-label={pageContent.hero.navLabel} ref={navRef}>
            <ul>
              {legalSections.map((section) => {
                const Icon = ICONS_BY_KEY[section.iconKey] ?? RiBookOpenLine;
                const isActive = activeSection === section.id;
                return (
                  <li
                    key={section.id}
                    className={`accent-${section.accent} ${isActive ? "is-active" : ""}`.trim()}
                  >
                    <a href={`#${section.id}`} onClick={(event) => handleNavClick(event, section.id)}>
                      <span className="policies-nav__icon" aria-hidden="true">
                        <Icon />
                      </span>
                      <span className="policies-nav__label">{section.title}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="policies-sections">
            {legalSections.map((section) => {
              const Icon = ICONS_BY_KEY[section.iconKey] ?? RiBookOpenLine;
              return (
                <section
                  key={section.id}
                  id={section.id}
                  className={`policies-card accent-${section.accent}`.trim()}
                  ref={(node) => {
                    sectionRefs.current[section.id] = node;
                  }}
                >
                  <header>
                    <span className="policies-card__icon" aria-hidden="true">
                      <Icon />
                    </span>
                    <div>
                      <p className="policies-card__eyebrow">{section.eyebrow}</p>
                      <h3>{section.title}</h3>
                    </div>
                  </header>
                  {section.blocks.map((block, index) => renderBlock(block, section.id, index))}
                </section>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PoliciesPage;
